import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TRANSLOCO_CONFIG, TRANSLOCO_LOADER, TranslocoModule, translocoConfig } from '@ngneat/transloco';
import { TranslocoHttpLoaderService } from './core/services/transloco-http-loader.service';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { AuthModule } from './modules/auth/auth.module';
import { HomeComponent } from './static-pages/home/home.component';
import { AdminLayoutComponent } from './layout/admin-layout/admin-layout.component';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { ItinerariesComponent } from './components/itineraries/itineraries.component';
import { ItineraryTemplatesComponent } from './components/itinerary-templates/itinerary-templates.component';
import { AddItineraryTemplateComponent } from './components/itinerary-templates/add-itinerary-template/add-itinerary-template.component';
import { UsersComponent } from './components/users/users.component';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { AddEditUserComponent } from './components/users/add-edit-user/add-edit-user.component';
import { RolesComponent } from './components/roles/roles.component';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { PreviewItineraryComponent } from './components/external/preview-itinerary/preview-itinerary.component';
import { AddRoleComponent } from './components/roles/add-role/add-role.component';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { TokenInterceptor } from './core/interceptors/http.interceptor';
import { EncryptionService } from './core/services/encryption.service';
import { AuthGuard } from './core/guards/auth.guard';
import { AuthService } from './modules/auth/services/auth.service';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { CustomersComponent } from './components/customers/customers.component';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { AddEditCustomerComponent } from './components/customers/add-edit-customer/add-edit-customer.component';
import { SettingsComponent } from './components/settings/settings.component';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { AccountComponent } from './components/settings/account/account.component';
import { AdminForgotPasswordService } from './modules/auth/services/admin-forgot-password.service';
import { DestinationsComponent } from './components/contents/destinations/destinations.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { McDestinationComponent } from './components/contents/destinations/mc-destination/mc-destination.component';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { AddDescriptionComponent } from './components/contents/shared/add-description/add-description.component';
import { AddImageComponent } from './components/contents/shared/add-image/add-image.component';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { BrandingComponent } from './components/settings/branding/branding.component';
import { OtherContentComponent } from './components/contents/other-content/other-content.component';
import { QuillModule } from 'ngx-quill';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzRateModule } from 'ng-zorro-antd/rate';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { AddEditItineryComponent } from './components/itineraries/add-edit-itinery/add-edit-itinery.component';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { ContentDrawerComponent } from './components/itineraries/content-drawer/content-drawer.component';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { AddEditDestinationContentComponent } from './components/itineraries/add-edit-content-modal/add-edit-destination-content/add-edit-destination-content.component';
import { ViewDestinationContentComponent } from './components/itineraries/view-content-modal/view-destination-content/view-destination-content.component';
import { AddEditAccommodationContentComponent } from './components/itineraries/add-edit-content-modal/add-edit-accommodation-content/add-edit-accommodation-content.component';
import { ViewAccommodationContentComponent } from './components/itineraries/view-content-modal/view-accommodation-content/view-accommodation-content.component';
import { AddEditTransportContentComponent } from './components/itineraries/add-edit-content-modal/add-edit-transport-content/add-edit-transport-content.component';
import { AddEditExperiencesContentComponent } from './components/itineraries/add-edit-content-modal/add-edit-experiences-content/add-edit-experiences-content.component';
import { ViewTransportContentComponent } from './components/itineraries/view-content-modal/view-transport-content/view-transport-content.component';
import { ViewExperiencesContentComponent } from './components/itineraries/view-content-modal/view-experiences-content/view-experiences-content.component';
import { ExperiencesComponent } from './components/contents/experiences/experiences.component';
import { McExperiencesComponent } from './components/contents/experiences/mc-experiences/mc-experiences.component';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import {
  AtSign,
  BedDouble,
  Blocks,
  CarFront,
  Copy,
  CreditCard,
  Earth,
  Globe,
  HandPlatter,
  LucideAngularModule,
  MoveDown,
  MoveRight,
  Phone,
  Plane,
  PlaneTakeoff,
  Plus,
  Sailboat,
  Sparkles,
  TramFront,
  Pencil,
  PencilOff,
  CircleUser,
  CornerUpRight,
  RefreshCw,
  DatabaseBackup,
  ArrowRight,
  ArrowLeft,
  Binoculars,
  Download,
  SendHorizontal,
  FilePlus,
  Eye,
  UserPlus,
  UserPen,
  UserMinus,
  Contact,
  User,
  FilePen,
  Trash2,
  TextSelect,
  SquareArrowOutUpRight,
  SquarePen,
  MapPinned,
  ChevronDown,
  CalendarCheck,
  SwatchBook,
  Flag,
  Star,
  Hand,
  WandSparkles,
  Upload,
  Cog,
  Info,
  Link,
  ExternalLink
} from 'lucide-angular';
import { SpacePipe } from './core/pipe/space.pipe';
import { SparklesSvgComponent } from './components/shared/sparkles-svg/sparkles-svg.component';
import { AdditionalNotesRichTextEditorComponent } from './components/shared/additional-notes-rich-text-editor/additional-notes-rich-text-editor.component';
import { AddImageModelComponent } from './components/itineraries/add-image-model/add-image-model.component';
import { ViewContentComponent } from './components/external/view-content/view-content.component';
import { AddEditPredefinedCostComponent } from './components/settings/preferences/tour-costing/add-edit-predefined-cost/add-edit-predefined-cost.component';
import { CostBreakdownComponent } from './components/itineraries/cost-breakdown/cost-breakdown.component';
import { AddEditCustomerInformationComponent } from './components/external/add-edit-customer-information/add-edit-customer-information.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { PreviewItinararyTwoComponent } from './components/external/preview-itinarary-two/preview-itinarary-two.component';
import { AgentModule } from './modules/agent/agent.module';
import { ViewAgentComponent } from './components/itineraries/view-agent/view-agent.component';
import { ViewStatusLogComponent } from './components/itineraries/view-status-log/view-status-log.component';
import { SharedModule } from './modules/shared/shared.module';
import { ChargebeeModule } from './modules/chargebee/chargebee.module';
import { BillingComponent } from './components/settings/billing/billing.component';
import { PreferencesComponent } from './components/settings/preferences/preferences.component';
import { ItinerarySettingsComponent } from './components/settings/preferences/itinerary-settings/itinerary-settings.component';
import { TourCostingComponent } from './components/settings/preferences/tour-costing/tour-costing.component';
import { PrefNotificationsComponent } from './components/settings/preferences/notifications/notifications.component';
import { PrefCategoriesComponent } from './components/settings/preferences/categories/categories.component';
import { CustomDomainComponent } from './components/settings/preferences/custom-domain/custom-domain.component';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { AddEditRemarksTitleComponent } from './components/itineraries/add-edit-remarks-title/add-edit-remarks-title.component';
import { TransportTimelineComponent } from './components/external/transport-timeline/transport-timeline.component';
import { PostHogErrorHandler } from './PostHogErrorHandler';
import { ExperiencesDragComponent } from './components/itineraries/experiences-drag/experiences-drag.component';
import { MoveDeleteDaysComponent } from './components/itineraries/move-delete-days/move-delete-days.component';
import { UploadImageComponent } from './components/settings/preferences/upload-image/upload-image.component';
import { ViewVehicleImageComponent } from './components/external/view-vehicle-image/view-vehicle-image.component';
import { ImagePositionerComponent } from './components/shared/image-positioner/image-positioner.component';

registerLocaleData(en);

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    AdminLayoutComponent,
    ItinerariesComponent,
    ItineraryTemplatesComponent,
    AddItineraryTemplateComponent,
    AddEditItineryComponent,
    UsersComponent,
    AddEditUserComponent,
    RolesComponent,
    AddRoleComponent,
    PreviewItineraryComponent,
    CustomersComponent,
    AddEditCustomerComponent,
    SettingsComponent,
    AccountComponent,
    DestinationsComponent,
    McDestinationComponent,
    AddDescriptionComponent,
    AddImageComponent,
    BrandingComponent,
    OtherContentComponent,
    ContentDrawerComponent,
    DashboardComponent,
    AddEditDestinationContentComponent,
    ViewDestinationContentComponent,
    AddEditAccommodationContentComponent,
    ViewAccommodationContentComponent,
    AddEditTransportContentComponent,
    AddEditExperiencesContentComponent,
    ViewTransportContentComponent,
    ViewExperiencesContentComponent,
    ExperiencesComponent,
    McExperiencesComponent,
    SpacePipe,
    SparklesSvgComponent,
    AdditionalNotesRichTextEditorComponent,
    AddImageModelComponent,
    ViewContentComponent,
    AddEditPredefinedCostComponent,
    CostBreakdownComponent,
    AddEditCustomerInformationComponent,
    UnauthorizedComponent,
    PreviewItinararyTwoComponent,
    ViewAgentComponent,
    ViewStatusLogComponent,
    BillingComponent,
    PreferencesComponent,
    ItinerarySettingsComponent,
    TourCostingComponent,
    PrefNotificationsComponent,
    PrefCategoriesComponent,
    CustomDomainComponent,
    AddEditRemarksTitleComponent,
    TransportTimelineComponent,
    ExperiencesDragComponent,
    MoveDeleteDaysComponent,
    UploadImageComponent,
    ViewVehicleImageComponent,
    ImagePositionerComponent,
  ],
  imports: [
    SharedModule,
    BrowserModule,
    TranslocoModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    AuthModule,
    NzIconModule,
    NzButtonModule,
    NzLayoutModule,
    NzMenuModule,
    NzSpinModule,
    NzBreadCrumbModule,
    NzTagModule,
    NzTableModule,
    NzDividerModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzPageHeaderModule,
    NzSpaceModule,
    NzAlertModule,
    NzCheckboxModule,
    NzSwitchModule,
    NzTabsModule,
    NzMessageModule,
    NgxDropzoneModule,
    NzListModule,
    NzUploadModule,
    NzModalModule,
    NzImageModule,
    QuillModule.forRoot(),
    NzRateModule,
    NzDropDownModule,
    NzDatePickerModule,
    NzCardModule,
    NzInputNumberModule,
    NzRadioModule,
    NzDrawerModule,
    NzAffixModule,
    NzBadgeModule,
    DragDropModule,
    NzTypographyModule,
    NgxWebstorageModule.forRoot(),
    NzToolTipModule,
    NzPopconfirmModule,
    LucideAngularModule.pick({
      CarFront,
      HandPlatter,
      BedDouble,
      PlaneTakeoff,
      AtSign,
      Phone,
      Earth,
      Blocks,
      Globe,
      Plus,
      Copy,
      Sparkles,
      TramFront,
      Plane,
      Sailboat,
      PencilOff,
      Pencil,
      CircleUser,
      CornerUpRight,
      RefreshCw,
      DatabaseBackup,
      ArrowRight,
      ArrowLeft,
      Binoculars,
      Download,
      SendHorizontal,
      FilePlus,
      Eye,
      UserPlus,
      UserPen,
      Contact,
      UserMinus,
      User,
      FilePen,
      Trash2,
      TextSelect,
      SquareArrowOutUpRight,
      SquarePen,
      MapPinned,
      ChevronDown,
      CalendarCheck,
      SwatchBook,
      Flag,
      Star,
      Hand,
      WandSparkles,
      Upload,
      Cog,
      Info,
      Link,
      ExternalLink
    }),
    AgentModule,
    ChargebeeModule,
    NzEmptyModule,
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: TRANSLOCO_CONFIG,
      useValue: translocoConfig({
        availableLangs: ['en', 'ru', 'es', 'de', 'fr'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      }),
    },
    {
      provide: TRANSLOCO_LOADER,
      useClass: TranslocoHttpLoaderService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: PostHogErrorHandler },
    EncryptionService,
    AuthGuard,
    AuthService,
    AdminForgotPasswordService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
